import { Link } from "react-router-dom";
import { ContentPageLayout } from "@/components/seo/ContentPageLayout";
import { SchemaMarkup } from "@/components/seo/SchemaMarkup";
import { FileText, Shield, Brain, Database, Users, Building, Clock, TrendingUp, CheckCircle } from "lucide-react";

const BASE = "https://sorvaine.com";

const faqItems = [
  { question: "What is RFP response software?", answer: "RFP response software helps teams draft, manage, and submit proposals faster by centralising content, automating compliance, and generating first drafts with AI." },
  { question: "How does Sorvaine work?", answer: "Upload an RFP, and Sorvaine extracts requirements, maps compliance, and generates structured first drafts. Your team refines the output rather than starting from scratch." },
  { question: "Do I need a content library to start?", answer: "No. Sorvaine generates drafts from the RFP itself. Your library builds automatically from every response you complete." },
  { question: "How long does setup take?", answer: "Minutes. There's no migration or onboarding process. Upload an RFP and start working immediately." },
  { question: "What types of RFPs does Sorvaine support?", answer: "Any structured request — RFPs, RFIs, tenders, and formal bids. The platform adapts to the document's structure regardless of format." },
  { question: "Is my data secure?", answer: "Yes. Sorvaine uses enterprise-grade encryption, SOC 2 compliance practices, and never shares your data with third parties." },
];

export default function RfpResponseSoftware() {
  return (
    <ContentPageLayout
      title="RFP Response Software — AI Proposal Generation | Sorvaine"
      description="Sorvaine automates RFP responses with AI. Upload your RFP, generate a tailored proposal outline, and export to Word in hours not days."
      canonical={`${BASE}/rfp-response-software`}
      breadcrumbs={[
        { name: "Home", url: BASE },
        { name: "RFP Response Software", url: `${BASE}/rfp-response-software` },
      ]}
      ctaHeading="See Sorvaine in action"
      ctaDescription="Book a 20-minute demo and see how your team can go from RFP to first draft in minutes."
    >
      <SchemaMarkup type="software" name="Sorvaine" description="AI-powered RFP response software that turns RFPs into submission-ready proposals." url={`${BASE}/rfp-response-software`} category="BusinessApplication" />
      <SchemaMarkup type="faq" items={faqItems} />

      <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.1]">
        RFP Response Software
      </h1>
      <p className="mt-4 text-lg text-white/50 max-w-[620px]">
        Sorvaine reads your RFP, extracts every requirement, and generates a structured first draft — so your team focuses on winning, not formatting.
      </p>

      {/* Problem */}
      <section className="mt-16">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">The RFP problem</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          A typical RFP response takes 20–40 hours of team time. Most of that time is spent searching for past content, coordinating contributors, and manually checking compliance — not writing compelling answers. When deadlines stack up, quality drops and winnable bids slip away.
        </p>
      </section>

      {/* Solution */}
      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">How Sorvaine solves it</h2>
        <p className="mt-3 text-white/50 leading-relaxed">
          Upload an RFP document. Within minutes, Sorvaine extracts every requirement, maps compliance obligations, and generates section-by-section first drafts. Your team refines and submits — instead of starting from a blank page.
        </p>
      </section>

      {/* Features */}
      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Features</h2>
        <div className="mt-6 grid sm:grid-cols-2 gap-5">
          {[
            { icon: FileText, title: "AI requirement extraction", desc: "Automatically pulls questions, criteria, and compliance requirements from any uploaded document." },
            { icon: Shield, title: "Compliance mapping", desc: "Checks your draft against every stated requirement so nothing gets missed." },
            { icon: Brain, title: "Answer generation", desc: "Generates first drafts tailored to each question using your organisation's knowledge and voice." },
            { icon: Database, title: "Knowledge base", desc: "Builds a searchable library automatically from every response you complete. No manual setup." },
          ].map((f) => (
            <div key={f.title} className="p-5 rounded-lg border border-white/[0.06] bg-white/[0.02]">
              <f.icon className="w-5 h-5 text-primary mb-3" />
              <h3 className="text-sm font-semibold text-white/80">{f.title}</h3>
              <p className="mt-1.5 text-sm text-white/40 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Who uses Sorvaine</h2>
        <div className="mt-6 space-y-5">
          {[
            { icon: Building, title: "Agencies", desc: "Responding to government tenders and enterprise RFPs with tight deadlines and complex requirements." },
            { icon: Users, title: "Consultancies", desc: "Pitching for advisory, implementation, or managed service contracts where differentiation matters." },
            { icon: FileText, title: "Bid and proposal teams", desc: "Dedicated teams managing high volumes of RFPs across multiple sectors and clients." },
          ].map((u) => (
            <div key={u.title} className="flex gap-4 items-start">
              <u.icon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-white/80">{u.title}</h3>
                <p className="mt-1 text-sm text-white/40">{u.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Benefits</h2>
        <div className="mt-6 space-y-4">
          {[
            { icon: Clock, text: "Cut response time by 40–60%. Reclaim hundreds of hours per year." },
            { icon: TrendingUp, text: "Improve win rates with consistent, high-quality responses on every bid." },
            { icon: CheckCircle, text: "Never miss a requirement. Automated compliance checks catch what humans overlook." },
          ].map((b, i) => (
            <div key={i} className="flex items-start gap-3">
              <b.icon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <p className="text-sm text-white/50">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Internal links */}
      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Compare Sorvaine</h2>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/bid-management-software" className="text-sm text-primary hover:underline">Bid Management Software →</Link>
          <Link to="/tender-management-software" className="text-sm text-primary hover:underline">Tender Management Software →</Link>
          <Link to="/loopio-alternatives" className="text-sm text-primary hover:underline">Sorvaine vs Loopio →</Link>
          <Link to="/rfpio-alternatives" className="text-sm text-primary hover:underline">Sorvaine vs RFPIO →</Link>
          <Link to="/best-rfp-software" className="text-sm text-primary hover:underline">Best RFP Software 2026 →</Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Frequently asked questions</h2>
        <div className="mt-6 space-y-6">
          {faqItems.map((item, i) => (
            <div key={i}>
              <h3 className="text-sm font-semibold text-white/70">{item.question}</h3>
              <p className="mt-1.5 text-sm text-white/40 leading-relaxed">{item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Related reading */}
      <section className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight text-white/90">Learn more</h2>
        <ul className="mt-4 space-y-2">
          <li><Link to="/resources/what-is-rfp-response-software" className="text-sm text-primary hover:underline">What Is RFP Response Software?</Link></li>
          <li><Link to="/resources/how-to-respond-to-an-rfp" className="text-sm text-primary hover:underline">How to Respond to an RFP: Step by Step</Link></li>
          <li><Link to="/resources/ai-rfp-software" className="text-sm text-primary hover:underline">How AI Is Transforming RFP Responses</Link></li>
        </ul>
      </section>
    </ContentPageLayout>
  );
}
